import {IGruppenDB} from "./InterfacesDB";
import {ITestheftAPI, TFach, TGruppentyp} from "./Interfaces";
import Tooltip from "./Tooltip";
import Utilities from "./Utilities";
import Veraportal from "./Veraportal";

export interface ITestheftinformation {
    testhefte: readonly ITestheftAPI[];
    bezeichnung: string;
    wert: string;
}

export default class FachGruppeFilter {
    private _veraportal: Veraportal;

    private domSlot: HTMLDivElement;
    private gruppenliste: IGruppenDB[];
    private _testheftinformation: ITestheftinformation = {
        testhefte: [],
        bezeichnung: 'Testheftvariante',
        wert: ''
    };
    public fach: TFach | 'alle';
    public gruppeId: number;

    public get testheftinformation(): ITestheftinformation {
        return this._testheftinformation;
    }

    constructor(veraportal: Veraportal, gruppenliste: IGruppenDB[] = [], fach: TFach = null, gruppeId: number = null, lerngruppenzugang: boolean = false) {
        this._veraportal = veraportal;
        this.domSlot = document.querySelector(`#fach-gruppe-filter`);
        if (this.domSlot === null) {
            console.error(`Für die Darstellung der Filterelemente ist ein div#fach-gruppe-filter erforderlich`);
            return;
        }

        this.gruppenliste = gruppenliste;
        this.fach = fach;
        this.gruppeId = gruppeId ?? null;

        this.domSlot.innerHTML = `
        <div class="filterauswahl" ${(lerngruppenzugang === true) ? `style="display: none;"` : ``}>
            <div class="fach">
                <label for="fach-filter">Fach:</label>
                <select id="fach-filter" class="fach-filter">
                    <option value="-" selected>Fach auswählen</option>
                    ${(this._veraportal.sessionvariablen.schule.teilnahme_mathematik !== 0) ? `<option value="ma">Mathematik</option>` : ``}
                    ${(this._veraportal.sessionvariablen.schule.teilnahme_deutsch !== 0) ? `<option value="de">Deutsch</option>` : ``}
                    ${(this._veraportal.sessionvariablen.schule.teilnahme_englisch !== 0) ? `<option value="en">Englisch</option>` : ``}
                    ${(this._veraportal.sessionvariablen.schule.teilnahme_franzoesisch !== 0) ? `<option value="fr">Französisch</option>` : ``}
                </select>
            </div>
            <div class="gruppe">
                <label for="gruppe-filter">Gruppe:</label>
                <select id="gruppe-filter" class="gruppe-filter" disabled="disabled">
                    <option value="-" selected>Gruppe auswählen</option>
                </select>
            </div>
        </div>
        <div class="hinweis-anzeige"></div>
        <section class="gruppeninformation" aria-label="Informationen zur gewählten Gruppe" hidden></section>
        `;
    }

    public setGruppeFach() {
        if (document.querySelectorAll(`#fach-filter`)?.length === 0 || document.querySelectorAll(`#gruppe-filter`)?.length === 0)
            return false;

        if (this.gruppeId !== null && this.gruppenliste.some(gruppe => gruppe.gruppe_id === this.gruppeId) === false) {
            this.gruppeId = null;
            window.location.replace(window.location.pathname);
            return false;
        }

        //region TomSelect zurücksetzen
        try {
            try {
                // @ts-ignore
                document.querySelector(`#fach-filter`)?.tomselect.destroy();
            } catch {
                //
            }

            // @ts-ignore
            new TomSelect(`#fach-filter`, {
                create: false,
                allowEmptyOption: true,
                controlInput: null,
                searchField: [],
                // onFocus: this.tsGruppeFocusFunction,
                onChange: async (value) => {
                    if (['ma', 'de', 'en', 'fr'].includes(value)) {
                        if (this.gruppeId !== null && this.gruppeIstImFach(this.gruppeId, value)) {
                            await this.auswahlNavigieren(value, this.gruppeId);
                            return;
                        }

                        this.gruppeId = null;
                        this.initGruppe(value);
                        // @ts-ignore
                        document.querySelector(`#gruppe-filter`)?.tomselect.enable();
                    } else {
                        // @ts-ignore
                        document.querySelector(`#gruppe-filter`)?.tomselect.disable();
                    }
                }
            });

            if (['ma', 'de', 'en', 'fr'].includes(this.fach)) {
                try {
                    // @ts-ignore
                    document.querySelector(`#fach-filter`)?.tomselect.setValue(this.fach, true);
                    this.initGruppe(this.fach);
                    // @ts-ignore
                    document.querySelector(`#gruppe-filter`)?.tomselect.enable();
                    // @ts-ignore
                    document.querySelector(`#gruppe-filter`)?.tomselect.setValue(this.gruppeId, true);
                } catch {
                    //
                }
            } else {
                this.initGruppe();
            }
        } catch (e) {
            Utilities.errorToSentry(e);
        }
        //endregion

        if (['ma', 'de', 'en', 'fr'].includes(this.fach) === false) {
            this.domSlot.querySelector(`div.hinweis-anzeige`).innerHTML =
                `Wählen Sie zuerst das Fach aus, bevor Sie Ergebnisse eingeben / ansehen können.`;
            return false;
        }

        if (this.gruppeId === null) {
            document.querySelector(`div.hinweis-anzeige`).innerHTML =
                `Wählen Sie zuerst die Gruppe aus, bevor Sie Ergebnisse eingeben / ansehen können.`;
            return false;
        }

        this.refreshGruppeninformation();

        return true;
    }

    /** Zeigt Zusammensetzung und Testheftverteilung der gewählten Fach-/Gruppenkombination */
    private refreshGruppeninformation(): void {
        const zugewieseneSchuelerIds = new Set(
            this._veraportal.schuelerGruppenZuweisungen
                .filter(zuweisung => zuweisung.gruppe_id === this.gruppeId)
                .map(zuweisung => zuweisung.schueler_id)
        );
        const schuelerInGruppe = this._veraportal.schuelerliste
            .filter(schueler => schueler.is_deaktiviert === 0 && zugewieseneSchuelerIds.has(schueler.schueler_id));
        const schuelerIds = new Set(schuelerInGruppe.map(schueler => schueler.schueler_id));
        const testhefteNachSchuelerId = new Map<number, ITestheftAPI>();

        this._veraportal.testliste
            .filter(test => schuelerIds.has(test.schueler_id) && test.fach === this.fach)
            .forEach(test => {
                const testheft = this._veraportal.testheftliste.find(eintrag => eintrag.testheft_id === test.testheft_id) ?? null;
                if (test.schueler_id === undefined || testheft === null || testhefteNachSchuelerId.has(test.schueler_id))
                    return;

                testhefteNachSchuelerId.set(test.schueler_id, testheft);
            });

        const testheftAnzahlen = new Map<number, number>();
        testhefteNachSchuelerId.forEach(testheft => {
            testheftAnzahlen.set(testheft.testheft_id, (testheftAnzahlen.get(testheft.testheft_id) ?? 0) + 1);
        });
        const testhefte = this._veraportal.testheftliste
            .filter(testheft => testheftAnzahlen.has(testheft.testheft_id))
            .sort((a, b) => a.bezeichnung.localeCompare(b.bezeichnung));
        const bezeichnung = (testhefte.length === 1) ? 'Testheftvariante' : 'Testheftvarianten';
        const wert = testhefte.map(testheft => `${testheft.bezeichnung} (${testheftAnzahlen.get(testheft.testheft_id)})`).join(', ');

        this._testheftinformation = {testhefte, bezeichnung, wert};

        const anzahlMaennlich = schuelerInGruppe.filter(schueler => schueler.geschlecht === 1).length;
        const anzahlWeiblich = schuelerInGruppe.filter(schueler => schueler.geschlecht === 2).length;
        const anzahlDivers = schuelerInGruppe.filter(schueler => schueler.geschlecht === 3).length;
        const anzahlOhneTestheft = schuelerInGruppe.length - testhefteNachSchuelerId.size;
        const testheftangaben = testhefte.map(testheft => `<li>${testheft.bezeichnung}${(testhefte.length > 1) ? `<span class="anzahl">${testheftAnzahlen.get(testheft.testheft_id)} Schüler</span>` : ``}</li>`);
        if (anzahlOhneTestheft > 0)
            testheftangaben.push(`<li>Ohne Testheft<span class="anzahl">${anzahlOhneTestheft} Schüler</span></li>`);

        const gruppeninformation = this.domSlot.querySelector<HTMLElement>(`section.gruppeninformation`);

        if (gruppeninformation !== null) {
            gruppeninformation.innerHTML = `
                <h2>Eigenschaften der gewählten Testgruppe:</h2>
                <dl>
                    <div>
                        <dt>Anzahl Schüler:</dt>
                        <dd><span class="schueleranzahl" data-tooltip="${Tooltip.html`
                            <p><strong>Geschlechterverteilung</strong></p>
                            <ul>
                                <li>Männlich: <strong>${anzahlMaennlich}</strong></li>
                                <li>Weiblich: <strong>${anzahlWeiblich}</strong></li>
                                <li>Divers: <strong>${anzahlDivers}</strong></li>
                            </ul>
                        `}">${schuelerInGruppe.length}</span></dd>
                    </div>
                    <div class="testheftverteilung">
                        <dt>${bezeichnung}:</dt>
                        <dd><ul>${testheftangaben.join('')}</ul></dd>
                    </div>
                </dl>
            `;
            gruppeninformation.hidden = false;
        }

        document.querySelectorAll<HTMLElement>(`[data-testheftinformation-label]`)
            .forEach(element => element.textContent = bezeichnung);
        document.querySelectorAll<HTMLElement>(`[data-testheftinformation-wert]`)
            .forEach(element => element.textContent = wert);
        document.querySelectorAll<HTMLElement>(`[data-testheftinformation-container]`)
            .forEach(element => element.hidden = wert === '');
    }

    private initGruppe(fach: string = null) {
        if (document.querySelectorAll(`#gruppe-filter`)?.length === 0) return;

        //region TomSelect zurücksetzen
        try {

            try {
                // @ts-ignore
                document.querySelector(`#gruppe-filter`).tomselect.destroy();
            } catch {
                //
            }

            //region HTML mit Optgroups vorgeben
            $(`#gruppe-filter`).html(
                `
            <option value="-" selected>Gruppe auswählen</option>
            <optgroup label="Stammklassen">
            ${
                    this.gruppenliste.filter((g) => {
                        return g.typ === 'STAMMKLASSE'
                    }).map((g) => {
                        // @ts-ignore
                        g.selected = false;
                        return g;
                    }).sort((a, b) => {
                        return a.bezeichnung.localeCompare(b.bezeichnung)
                    }).map(this.gruppeOptionHTML).join('')
                }
            </optgroup>
            
            ${(fach === null || fach === 'ma') ? `
            <optgroup label="Kurse: Mathematik">
            ${
                    this.gruppenliste.filter((g) => {
                        return ['KURS', 'LERNGRUPPE'].includes(g.typ) && g.kurs_fach === 'ma'
                    }).map((g) => {
                        // @ts-ignore
                        g.selected = false;
                        return g;
                    }).sort((a, b) => {
                        return a.bezeichnung.localeCompare(b.bezeichnung)
                    }).map(this.gruppeOptionHTML).join('')
                }
            </optgroup>
            ` : ``}
            
            
            ${(fach === null || fach === 'de') ? `
            <optgroup label="Kurse: Deutsch">
            ${
                    this.gruppenliste.filter((g) => {
                        return ['KURS', 'LERNGRUPPE'].includes(g.typ) && g.kurs_fach === 'de'
                    }).map((g) => {
                        // @ts-ignore
                        g.selected = false;
                        return g;
                    }).sort((a, b) => {
                        return a.bezeichnung.localeCompare(b.bezeichnung)
                    }).map(this.gruppeOptionHTML).join('')
                }
            </optgroup>
            ` : ``}
            
            
            ${(fach === null || fach === 'en') ? `
            <optgroup label="Kurse: Englisch">
            ${
                    this.gruppenliste.filter((g) => {
                        return ['KURS', 'LERNGRUPPE'].includes(g.typ) && g.kurs_fach === 'en'
                    }).map((g) => {
                        // @ts-ignore
                        g.selected = false;
                        return g;
                    }).sort((a, b) => {
                        return a.bezeichnung.localeCompare(b.bezeichnung)
                    }).map(this.gruppeOptionHTML).join('')
                }
            </optgroup>
            ` : ``}
            
            
            ${(fach === null || fach === 'fr') ? `
            <optgroup label="Kurse: Französisch">
            ${
                    this.gruppenliste.filter((g) => {
                        return ['KURS', 'LERNGRUPPE'].includes(g.typ) && g.kurs_fach === 'fr'
                    }).map((g) => {
                        // @ts-ignore
                        g.selected = false;
                        return g;
                    }).sort((a, b) => {
                        return a.bezeichnung.localeCompare(b.bezeichnung)
                    }).map(this.gruppeOptionHTML).join('')
                }
            </optgroup>
            ` : ``}
        `);
            //endregion

            // @ts-ignore
            new TomSelect(`#gruppe-filter`, {
                create: false,
                allowEmptyOption: true,
                controlInput: null,
                searchField: [],
                render: {
                    option: function (data: { icon: string; text: string; gruppeid: string; typ: TGruppentyp }, escape) {
                        return `<div class="gruppe-option">
                        <div class="icon">
                        ${(data.icon !== null) ? `
                            <svg role='graphics-symbol'>
                                <use href='/lib/bootstrap-icons/bootstrap-icons/bootstrap-icons.svg#${data.icon}'/>
                            </svg>
                        ` : ``}
                        </div>
                        <div class="text">
                            <div class="bezeichnung">${data.text}</div>
                        </div>
                        <div class="options"></div>
                    </div>`;
                    },
                    optgroup_header: function (data, escape) {
                        return '<div class="optgroup-header">' + escape(data.label) + '</div>';
                    }
                },
                // onFocus: this.tsGruppeFocusFunction,
                onChange: async (value) => {
                    if (value !== '-') {
                        await this.auswahlNavigieren($(`#fach-filter`).val() as TFach, Number(value));
                    }
                }
            });
        } catch (e) {
            Utilities.errorToSentry(e);
        }
        //endregion
    }

    private gruppeIstImFach(gruppeId: number, fach: TFach): boolean {
        const gruppe = this.gruppenliste.find(g => g.gruppe_id === gruppeId);
        if (gruppe === undefined)
            return false;

        return gruppe.typ === 'STAMMKLASSE' || (
            ['KURS', 'LERNGRUPPE'].includes(gruppe.typ) && gruppe.kurs_fach === fach
        );
    }

    private async auswahlNavigieren(fach: TFach, gruppeId: number): Promise<void> {
        await this._veraportal.lifecycleLeave(); // vor Seitenwechsel Daten speichern
        const readonlyParameter = new URLSearchParams(window.location.search).get('readonly');
        window.location.href = `?fach=${fach}&gruppeid=${gruppeId}${(readonlyParameter !== null) ? `&readonly=${encodeURIComponent(readonlyParameter)}` : ''}`;
    }

    private gruppeOptionHTML = (
        {gruppe_id, bezeichnung, typ, selected}: IGruppenDB & { selected: boolean }
    ) => {
        return `<option value="${gruppe_id}" ${((selected ?? false)) ? `selected="selected"` : ``} ${(typ === 'STAMMKLASSE') ? `data-icon="people-fill"` : `data-icon="calendar2-week"`} data-gruppeid="${gruppe_id}" data-typ="${typ}">${bezeichnung}</option>`;
    };
}
